import apiServiceGenerator from "@/api/generator";
import {changes, loading, alert, handleError, normalizeFilters} from './helpers'

export default function (endpoint) {

    // Az `all` action eredményeinek cache-e szűrőnként. Szándékosan nem a state-ben van,
    // hogy a nagy listák ne legyenek feleslegesen reaktívak.
    let cache = {};
    // A folyamatban lévő `all` kérések szűrőnként (párhuzamos azonos kérések összevonására).
    let pending = {};
    // Az utoljára kért szűrő kulcsa: csak ennek a válasza írhatja a state.all-t.
    let lastAllKey = null;

    const clone = (value) => JSON.parse(JSON.stringify(value ?? []));

    return {
        namespaced: true,

        state: {
            loading: false,
            pager: {},
            additional: {},
            list: [],
            all: [],
            allQuery: {},
            item: {},
            errors: {},
            fields: [],
            service: apiServiceGenerator(endpoint),
        },

        getters: {
            loading: function (state) {
                return !!state.loading
            },
            pager: function (state) {
                if (!state.pager) {
                    console.log(state.pager);
                }
                return JSON.parse(JSON.stringify(state.pager));
            },
            all: function (state) {
                if (!state.all) {
                    console.log(state.all);
                }
                return JSON.parse(JSON.stringify(state.all))
            },
            list: function (state) {
                return state.list
            },
            item: function (state) {
                if (!state.item) {
                    console.log(state.item);
                }
                return JSON.parse(JSON.stringify(state.item))
            },
            additional: function (state) {
                if (!state.additional) {
                    console.log(state.additional);
                }
                return JSON.parse(JSON.stringify(state.additional))
            },
            states: function (state) {
                const states = {};

                Object.keys(state.errors).forEach(key => {
                        states[key] = false
                    }
                )

                return states
            },
            ordered: (state) => (prop) => {
                return JSON.parse(JSON.stringify(state.list)).sort(function (a,b) {
                    return a[prop] < b[prop]
                        ? -1
                        : (
                            a[prop] > b[prop]
                            ? 1
                            : 0
                        )
                })
            },
            errors: function (state) {
                const errors = Object.assign({}, (state.errors || {}))

                Object.keys(errors).forEach(key => {
                    errors[key] = errors[key].join(' ')
                })

                return errors;
            },
            fields: function (state) {
                return state.fields
            },
        },

        mutations: {
            'SET_LOADING': function (state, boolean = true) {
                state.loading = boolean
            },

            'SET_ALL_QUERY': function (state, query = {}) {
                state.allQuery = query
            },

            'SET_ALL': function (state, all) {
                state.all = all
            },

            'SET_LIST': function (state, list) {
                state.list = list
            },

            'SET_ITEM': function (state, item = {}) {
                state.item = item
            },

            'UPDATE_ITEM': function (state, props) {
                Object.keys(props).forEach(prop => {
                    state.item[prop] = props[prop]
                })
            },

            'ADD_ITEM': function (state, item) {
                if (!state.list) {
                    state.list = [];
                }

                state.list.unshift(item)
            },

            'PUSH_ITEM': function (state, item) {
                if (!state.list) {
                    state.list = []
                }

                state.list.push(item)
                state.all.push(item)
            },

            'REPLACE_ITEM': function (state, item) {
                const index = state.list.findIndex(listItem => listItem.id === item.id);

                if (index !== -1) {
                    state.list[index] = item
                    state.list = JSON.parse(JSON.stringify(state.list))
                }

                const allIndex = state.all.findIndex(allItem => allItem.id === item.id);

                if (allIndex !== -1) {
                    state.all[allIndex] = item
                    state.all = JSON.parse(JSON.stringify(state.all))
                }
            },

            'REMOVE_ITEM': function (state, id) {
                const index = state.list.findIndex(item => item.id === id);

                if (index !== -1) {
                    state.list.splice(index, 1)
                }

                const allIndex = state.all.findIndex(item => item.id === id);

                if (allIndex !== -1) {
                    state.all.splice(allIndex, 1)
                }
            },

            'SET_PAGER': function (state, pager = {}) {
                state.pager = pager
            },

            'SET_ADDITIONAL': function (state, data = {}) {
                state.additional = data
            },

            'SET_ERRORS'(state, errors) {
                state.errors = errors
            },

            'SET_ERROR'(state, error) {
                const errors = Object.assign({}, state.errors)

                Object.keys(error).forEach(key => {
                    errors[key]
                        ? errors[key].push(error[key])
                        : errors[key] = Array(error[key])
                })

                state.errors = errors
            },

            'SET_FIELDS': function(state, fields) {
                state.fields = fields
            }
        },

        actions: {
            all: function ({commit, state, getters}, force) {
                if (state.all && state.all.length && !force) {
                    return getters.all;
                }

                const data = typeof force === 'object' ? force : undefined;
                const key = normalizeFilters(data);
                // force === true: kifejezett frissítés kérés, a cache-t és a folyamatban lévő kérést is megkerüli
                const refresh = force === true;

                lastAllKey = key;

                if (data) {
                    commit('SET_ALL_QUERY', data);
                }

                if (!refresh && cache[key]) {
                    const cached = clone(cache[key]);
                    commit('SET_ALL', cached);
                    return cached;
                }

                if (!refresh && pending[key]) {
                    return pending[key];
                }

                loading(commit);

                const request = state.service.fetchAll(data).then(response => {
                    cache[key] = clone(response.data);

                    if (lastAllKey === key) {
                        commit('SET_ALL', response.data);
                    }

                    loading(commit, false);
                    return response.data;
                }).catch((e) => {
                    handleError(e, commit);
                }).finally(() => {
                    if (pending[key] === request) {
                        delete pending[key];
                    }
                });

                pending[key] = request;

                return request;
            },

            clearCache: function () {
                cache = {};
            },

            list: function ({commit, state}, filters) {
                loading(commit)

                return state.service.fetchList(filters).then(response => {
                    commit('SET_LIST', response.data)
                    commit('SET_PAGER', response.meta)
                    commit('SET_ADDITIONAL', response.additional)
                    loading(commit, false)
                    return response.data
                }).catch((e) => {
                    handleError(e, commit)
                })
            },

            show: function ({commit, state}, {id, query}) {
                loading(commit)

                return state.service.fetchItem(id, query).then(data => {
                    commit('SET_ITEM', data.data)
                    loading(commit, false)
                    return data
                }).catch((e) => {
                    handleError(e, commit)
                })
            },

            select: function ({commit, state}, id) {
                loading(commit)

                let item = state.list.find(item => item.id === id);

                if (!item) {
                    item = state.all.find(item => item.id === id)
                }

                if (item) {
                    commit('SET_ITEM', item);
                    loading(commit, false)
                    return item;
                } else {
                    loading(commit, false)
                }
            },

            create: function ({commit, state, /*dispatch*/}, payload) {
                loading(commit)

                return state.service.store(payload).then(data => {
                    const model = Object.assign({}, payload, data.data)

                    cache = {};
                    commit('ADD_ITEM', model);
                    commit('SET_ITEM', model);
                    alert(commit, this.$t('request.creation_is_successful'));
                    commit('SET_ERRORS', {});
                    // dispatch('all', state.allQuery);

                    loading(commit, false)

                    return model
                }).catch((e) => {
                    handleError(e, commit)
                })
            },

            createConfig: function ({commit, state, dispatch}, { push, payload}) {
                loading(commit)

                return state.service.store(payload).then(data => {
                    const model = Object.assign({}, payload, data.data)

                    cache = {};
                    commit(push ? 'PUSH_ITEM' : 'ADD_ITEM', model)
                    commit('SET_ITEM', model)
                    alert(commit, this.$t('request.creation_is_successful'))
                    commit('SET_ERRORS', {});
                    dispatch('all', true);

                    loading(commit, false)

                    return model
                }).catch((e) => {
                    handleError(e, commit)
                })
            },

            update: function ({commit, state}, payload) {
                loading(commit)

                const { id, data } = changes(payload, state.item)

                const content = Object.assign({_method: 'PUT'}, data)

                return state.service.update(id, content).then(response => {
                    const model = Object.assign({}, data, response.data)

                    cache = {};
                    commit('REPLACE_ITEM', model);
                    commit('SET_ITEM', model);
                    alert(commit, this.$t('request.save_is_successful'))
                    commit('SET_ERRORS', {});
                    loading(commit, false)

                    return model;
                }).catch((e) => {
                    handleError(e, commit)
                })
            },

            destroy: function ({commit, state}, id) {
                loading(commit)

                return state.service.destroy(id).then(response => {
                    cache = {};
                    commit('REMOVE_ITEM', id);
                    commit('SET_ITEM', {});
                    alert(commit, this.$t('request.deletion_is_successful'))
                    loading(commit, false)

                    return response;
                }).catch((e) => {
                    handleError(e, commit)
                });
            },

            push: function ({commit}, item) {
                cache = {};
                commit('PUSH_ITEM', item)
            },

            setItem: function ({commit}, payload) {
                commit('SET_ITEM', payload)
            },

            setAll: function ({commit}, payload) {
                commit('SET_ALL', payload)
            },

            clearItem: function ({commit}) {
                commit('SET_ITEM')
            },

            clearList: function ({commit}) {
                commit('SET_LIST', [])
                commit('SET_PAGER', {})
                commit('SET_ADDITIONAL', {})
            },

            clearAll: function ({commit}) {
                lastAllKey = null;
                commit('SET_ALL', [])
            },

            clearErrors: function ({commit}) {
                commit('SET_ERRORS', {})
            },

            clearModule: function ({dispatch}) {
                dispatch('clearItem')
                dispatch('clearList')
                dispatch('clearAll')
                dispatch('clearErrors')
            },

            replaceItem: function ({commit}, item) {
                cache = {};
                commit('REPLACE_ITEM', item);
            },

            fields: function ({commit, rootState}, payload) {
                loading(commit)

                return rootState.field.service.fetchList(payload).then(response => {
                    commit('SET_FIELDS', response.data)
                    loading(commit, false)
                    return response.data
                }).catch((e) => {
                    handleError(e, commit)
                })
            },
        },
    }
}
